import { useState } from "react";
import { Upload, Monitor, Tv, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { uploadFile, fileUrl } from "@/lib/upload";
import { PHASES, FORMATS, allFormats, formatRatio } from "@/components/ScreenMedia";
import { ScreenAiDialog } from "@/components/ScreenAiDialog";
import { ScreensAllAiDialog } from "@/components/ScreensAllAiDialog";
import { CustomFormatsEditor } from "@/components/CustomFormatsEditor";
import { ScreenPreviewDialog } from "@/components/ScreenPreviewDialog";

const isVideoSrc = (src, value) => /\.(mp4|webm)/i.test(src) || (value?.file_id && !/\.(gif|png|jpe?g|webp)/i.test(src) && value?.is_video);

function CellPreview({ src, value }) {
  if (!src) return "vuoto";
  if (isVideoSrc(src, value)) return <video src={src} muted loop autoPlay className="w-full h-full object-cover" />;
  return <img src={src} alt="" className="w-full h-full object-cover" />;
}

function Cell({ contestId, phase, format, ratio, value, onChange }) {
  const [prog, setProg] = useState(null);
  const src = fileUrl(value?.file_id) || value?.url;
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setProg(0);
    try {
      const doc = await uploadFile(f, `screen_${phase}_${format}`, setProg);
      onChange({ file_id: doc.id, url: null });
      toast.success("Videata caricata");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setProg(null);
    }
  };
  return (
    <div className="space-y-1.5" data-testid={`screen-cell-${phase}-${format}`}>
      <div style={{ aspectRatio: ratio, maxHeight: 160 }} className="w-full mx-auto rounded-lg bg-secondary/60 border border-dashed border-border overflow-hidden grid place-items-center text-[10px] text-muted-foreground">
        <CellPreview src={src} value={value} />
      </div>
      <label className="block cursor-pointer">
        <input type="file" accept="image/gif,image/png,image/jpeg,image/webp,video/mp4,video/webm" onChange={pick} className="hidden" data-testid={`screen-upload-${phase}-${format}`} />
        <div className="h-7 rounded-md border border-border grid place-items-center text-[11px] hover:border-primary">
          {prog !== null ? `${prog}%` : <span className="flex items-center gap-1"><Upload className="h-3 w-3" /> GIF / video</span>}
        </div>
      </label>
      <ScreenAiDialog contestId={contestId} phase={phase} format={format} ratio={ratio} onGenerated={(doc) => onChange({ file_id: doc.id, url: null })} />
      <Input value={value?.url || ""} onChange={(e) => onChange({ file_id: null, url: e.target.value || null })} placeholder="o URL" className="h-7 text-[11px]" data-testid={`screen-url-${phase}-${format}`} />
      {(value?.file_id || value?.url) && <button onClick={() => onChange(null)} className="text-[10px] text-muted-foreground underline">rimuovi</button>}
    </div>
  );
}

export default function ScreensEditor({ contestId, game, setGame }) {
  const screens = game.screens || {};
  const custom = game.custom_formats || [];
  const formats = allFormats(custom);
  const set = (phase, format, v) => {
    const next = { ...screens, [phase]: { ...(screens[phase] || {}) } };
    if (v) next[phase][format] = v; else delete next[phase][format];
    setGame({ ...game, screens: next });
  };
  const base = window.location.origin;
  const links = [
    ["TV / monitor esterno (16:9)", `${base}/display/${contestId}?format=landscape`],
    ["Totem verticale (9:16)", `${base}/display/${contestId}?format=portrait`],
    ["Auto (adatta allo schermo)", `${base}/display/${contestId}`],
  ];
  return (
    <Card className="p-5 bg-card border-border space-y-4" data-testid="screens-editor">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-display font-bold text-lg flex items-center gap-2"><Tv className="h-4 w-4" /> Videate animate (GIF / video)</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Carica una GIF animata, immagine o video per ogni fase e formato, oppure usa "Genera con AI" per creare lo sfondo (statico o GIF animata) nel formato esatto della cella. Il totem, l'embed e lo schermo esterno scelgono automaticamente il formato più adatto ai pixel dello schermo (se manca, usano il primo disponibile).
          </p>
        </div>
        <ScreensAllAiDialog contestId={contestId} customFormats={custom} onApply={(all) => setGame({ ...game, screens: all })} />
      </div>
      <CustomFormatsEditor custom={custom} onChange={(list) => setGame({ ...game, custom_formats: list })} baseUrl={`${window.location.origin}/display/${contestId}`} />
      <div className="flex items-center gap-3">
        <Label className="text-xs">Adattamento</Label>
        <select data-testid="screens-fit" value={game.screens_fit || "cover"} onChange={(e) => setGame({ ...game, screens_fit: e.target.value })} className="h-8 rounded-md border border-border bg-background px-2 text-xs">
          <option value="cover">Riempi lo schermo (ritaglia)</option>
          <option value="contain">Mostra intero (bande)</option>
        </select>
        <label className="flex items-center gap-2 text-xs ml-auto">
          <input type="checkbox" data-testid="show-leaderboard" checked={game.show_leaderboard !== false} onChange={(e) => setGame({ ...game, show_leaderboard: e.target.checked })} />
          Classifica sedi sul display esterno
        </label>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-muted-foreground">
              <th className="py-1 pr-2 w-32">Fase</th>
              {formats.map(([k, l]) => (
                <th key={k} className="py-1 px-1 font-semibold">
                  {l}
                  <div><ScreenPreviewDialog contestId={contestId} game={game} format={k} label={l} ratio={formatRatio(k, custom)} /></div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PHASES.map(([pk, pl]) => (
              <tr key={pk} className="border-t border-border align-top">
                <td className="py-3 pr-2 font-semibold">{pl}</td>
                {formats.map(([fk]) => (
                  <td key={fk} className="py-3 px-1" style={{ width: `${Math.floor(85 / formats.length)}%` }}><Cell contestId={contestId} phase={pk} format={fk} ratio={formatRatio(fk, custom)} value={screens[pk]?.[fk]} onChange={(v) => set(pk, fk, v)} /></td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="rounded-lg bg-secondary/40 p-3 space-y-2">
        <Label className="text-xs flex items-center gap-1.5"><Monitor className="h-3.5 w-3.5" /> Link schermo esterno (aprire a tutto schermo sul PC del totem; aggiungi <span className="font-mono-code">&sede=ID</span> per filtrare le giocate di una sede)</Label>
        {links.map(([l, u]) => (
          <div key={u} className="flex items-center gap-2">
            <span className="text-[11px] w-44 text-muted-foreground">{l}</span>
            <Input readOnly value={u} className="h-7 text-[11px] font-mono-code" data-testid="display-link" />
            <Button size="sm" variant="ghost" className="h-7" onClick={() => { navigator.clipboard.writeText(u); toast.success("Link copiato"); }}><Copy className="h-3 w-3" /></Button>
          </div>
        ))}
      </div>
    </Card>
  );
}
